import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type {
  AssignUsersPayload,
  CreateCustomRolePayload,
  CustomRole,
  CustomRoleDetail,
  EffectiveAccess,
  PermissionModule,
  RbacMenu,
  RoleSubject,
  SubjectType,
  UpdateCustomRolePayload,
} from '@shared-types/rbac';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: { code?: string; message?: string };
}

export const rbacQueryKeys = {
  all: ['rbac'] as const,
  me: ['rbac', 'me'] as const,
  permissions: ['rbac', 'permissions'] as const,
  menus: ['rbac', 'menus'] as const,
  roles: (importirNpwp?: string) => ['rbac', 'roles', importirNpwp ?? 'all'] as const,
  role: (id: number) => ['rbac', 'role', id] as const,
  subjects: (subjectType: SubjectType, search: string) => ['rbac', 'subjects', subjectType, search] as const,
};

/** Surfaces the backend's error message rather than a generic string. */
function errorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: ApiEnvelope<unknown> } })?.response?.data;
  return response?.error?.message || response?.message || fallback;
}

function notifyError(error: unknown, fallback: string) {
  notifications.show({ title: 'Error', message: errorMessage(error, fallback), color: 'red' });
}

export function useGetCustomRoles(importirNpwp?: string, options?: { enabled?: boolean; includeInactive?: boolean }) {
  return useQuery({
    queryKey: [...rbacQueryKeys.roles(importirNpwp), options?.includeInactive ?? false],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      if (importirNpwp) params.set('importirNpwp', importirNpwp);
      if (options?.includeInactive) params.set('includeInactive', 'true');
      const query = params.toString();
      const response = await axiosInstance.get<ApiEnvelope<CustomRole[]>>(
        query ? `${apiUrl.rbac.roles}?${query}` : apiUrl.rbac.roles,
        { signal }
      );
      return response.data.data ?? [];
    },
    enabled: options?.enabled ?? true,
  });
}

export function useGetCustomRole(id: number | null) {
  return useQuery({
    queryKey: rbacQueryKeys.role(id ?? 0),
    queryFn: async ({ signal }) => {
      const response = await axiosInstance.get<ApiEnvelope<CustomRoleDetail>>(apiUrl.rbac.role(id as number), {
        signal,
      });
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useGetPermissions() {
  return useQuery({
    queryKey: rbacQueryKeys.permissions,
    queryFn: async ({ signal }) => {
      const response = await axiosInstance.get<ApiEnvelope<PermissionModule[]>>(apiUrl.rbac.permissions, { signal });
      return response.data.data ?? [];
    },
    // The catalog only changes on deploy — no reason to refetch per mount.
    staleTime: 30 * 60 * 1000,
  });
}

export function useGetRbacMenus() {
  return useQuery({
    queryKey: rbacQueryKeys.menus,
    queryFn: async ({ signal }) => {
      const response = await axiosInstance.get<ApiEnvelope<RbacMenu[]>>(apiUrl.rbac.menus, { signal });
      return response.data.data ?? [];
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useGetAssignableUsers(subjectType: SubjectType = 'USER', search = '', options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: rbacQueryKeys.subjects(subjectType, search),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ subjectType });
      if (search.trim()) params.set('search', search.trim());
      const response = await axiosInstance.get<ApiEnvelope<RoleSubject[]>>(
        `${apiUrl.rbac.subjects}?${params.toString()}`,
        { signal }
      );
      return response.data.data ?? [];
    },
    enabled: options?.enabled ?? true,
  });
}

export function useCreateCustomRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCustomRolePayload) => {
      const response = await axiosInstance.post<ApiEnvelope<CustomRoleDetail>>(apiUrl.rbac.roles, payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.all });
      notifications.show({ title: 'Berhasil', message: 'Role berhasil dibuat', color: 'green' });
    },
    onError: (error) => notifyError(error, 'Gagal membuat role'),
  });
}

export function useUpdateCustomRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateCustomRolePayload }) => {
      const response = await axiosInstance.put<ApiEnvelope<CustomRoleDetail>>(apiUrl.rbac.role(id), data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.role(variables.id) });
      notifications.show({ title: 'Berhasil', message: 'Role berhasil diperbarui', color: 'green' });
    },
    onError: (error) => notifyError(error, 'Gagal memperbarui role'),
  });
}

export function useDeleteCustomRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await axiosInstance.delete<ApiEnvelope<{ id: number }>>(apiUrl.rbac.role(id));
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.all });
      notifications.show({ title: 'Berhasil', message: 'Role dinonaktifkan', color: 'green' });
    },
    // The backend refuses while active users are still assigned; surface that
    // message verbatim so the admin knows to reassign them first.
    onError: (error) => notifyError(error, 'Gagal menghapus role'),
  });
}

export function useAssignUsersToRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AssignUsersPayload }) => {
      const response = await axiosInstance.post<ApiEnvelope<CustomRoleDetail>>(apiUrl.rbac.roleUsers(id), data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.role(variables.id) });
      notifications.show({ title: 'Berhasil', message: 'Penugasan user diperbarui', color: 'green' });
    },
    onError: (error) => notifyError(error, 'Gagal memperbarui penugasan user'),
  });
}

/**
 * Effective permissions for the signed-in user. Used by PermissionsProvider;
 * components should read `useHasPermission` instead of calling this directly.
 */
export function useGetMyAccess() {
  return useQuery({
    queryKey: rbacQueryKeys.me,
    queryFn: async ({ signal }) => {
      const response = await axiosInstance.get<ApiEnvelope<EffectiveAccess>>(apiUrl.rbac.me, { signal });
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
